/**
 * AbuseSignals.tsx — Sprint 63
 *
 * Surfaces the Sprint 36 D3 anti-cheat abuse-signal admin queue.
 *
 * Endpoints:
 *   GET  /admin/abuse-signals?status=open|reviewed|all&severity=…&signal_type=…
 *   POST /admin/abuse-signals/:signalId/review  { disposition, notes? }
 *
 * Renders:
 *   - Status (open/reviewed/all), severity, and signal_type filters
 *   - Table: severity | type | gang | detected age | disposition | actions
 *   - Expandable details payload (JSONB)
 *   - Review dialog with disposition select (dismiss/warn/throttle/suspend)
 *     and optional notes.
 *
 * States: loading, error, empty, endpoint-missing.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  listAbuseSignals,
  reviewAbuseSignal,
  AbuseSignal,
  AbuseSignalSeverity,
  AbuseSignalDisposition,
  AdminApiError,
} from '../api/admin-client';
import ConfirmDialog from '../components/ConfirmDialog';

const STATUS_OPTIONS: Array<'open' | 'reviewed' | 'all'> = ['open', 'reviewed', 'all'];
const SEVERITY_OPTIONS: Array<'' | AbuseSignalSeverity> = ['', 'low', 'medium', 'high'];
const DISPOSITIONS: AbuseSignalDisposition[] = ['dismiss', 'warn', 'throttle', 'suspend'];

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 2) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function severityBadge(severity: AbuseSignalSeverity) {
  const cls =
    severity === 'high'   ? 'badge-red'   :
    severity === 'medium' ? 'badge-amber' :
                            'badge-gray';
  return <span className={`badge ${cls}`}>{severity}</span>;
}

function dispositionBadge(d?: AbuseSignalDisposition | null) {
  if (!d) return <span className="badge badge-blue">pending review</span>;
  const cls =
    d === 'suspend'  ? 'badge-red'   :
    d === 'throttle' ? 'badge-amber' :
    d === 'warn'     ? 'badge-amber' :
                       'badge-gray';
  return <span className={`badge ${cls}`}>{d}</span>;
}

export default function AbuseSignals() {
  const [status, setStatus]         = useState<'open' | 'reviewed' | 'all'>('open');
  const [severity, setSeverity]     = useState<'' | AbuseSignalSeverity>('');
  const [signalType, setSignalType] = useState<string>('');
  const [signals, setSignals] = useState<AbuseSignal[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [endpointMissing, setEndpointMissing] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Review dialog state
  const [reviewTarget, setReviewTarget] = useState<AbuseSignal | null>(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState('');
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setEndpointMissing(false);
    try {
      const data = await listAbuseSignals({
        status,
        severity: severity || undefined,
        signal_type: signalType.trim() || undefined,
        limit: 200,
      });
      setSignals(data);
    } catch (err: any) {
      if (err instanceof AdminApiError && (err.status === 404 || err.status === 501)) {
        setEndpointMissing(true);
      } else {
        setError(err?.message ?? 'Failed to load abuse signals');
      }
    } finally {
      setLoading(false);
    }
  }, [status, severity, signalType]);

  useEffect(() => { load(); }, [load]);

  function toggleExpand(id: string) {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function openReview(signal: AbuseSignal) {
    setReviewTarget(signal);
    setReviewError('');
    // Default disposition based on severity to nudge proportional response
    const defaultDisp: AbuseSignalDisposition =
      signal.severity === 'high'   ? 'suspend'  :
      signal.severity === 'medium' ? 'throttle' :
                                     'warn';
    setFieldValues({ disposition: defaultDisp, notes: '' });
  }

  async function executeReview() {
    if (!reviewTarget) return;
    const disposition = (fieldValues.disposition ?? 'dismiss') as AbuseSignalDisposition;
    const notes = fieldValues.notes ?? '';
    setReviewLoading(true);
    setReviewError('');
    try {
      await reviewAbuseSignal(reviewTarget.signal_id, disposition, notes || undefined);
      // Optimistic: remove from list if filter is 'open', else refresh
      if (status === 'open') {
        setSignals(s => s.filter(row => row.signal_id !== reviewTarget.signal_id));
      } else {
        await load();
      }
      setReviewTarget(null);
    } catch (err: any) {
      if (err instanceof AdminApiError && err.code === 'signal_already_reviewed') {
        setReviewError('This signal has already been reviewed. Refresh to see latest state.');
      } else {
        setReviewError(err?.message ?? 'Review failed');
      }
    } finally {
      setReviewLoading(false);
    }
  }

  return (
    <div>
      <div className="page-title">Abuse Signals Queue</div>

      {/* Filters */}
      <div
        style={{
          display: 'flex',
          gap: 12,
          marginBottom: 20,
          alignItems: 'flex-end',
          flexWrap: 'wrap',
        }}
      >
        <div className="form-group" style={{ marginBottom: 0, minWidth: 140 }}>
          <label htmlFor="as-status">Status</label>
          <select
            id="as-status"
            value={status}
            onChange={(e) => setStatus(e.target.value as any)}
          >
            {STATUS_OPTIONS.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div className="form-group" style={{ marginBottom: 0, minWidth: 140 }}>
          <label htmlFor="as-severity">Severity</label>
          <select
            id="as-severity"
            value={severity}
            onChange={(e) => setSeverity(e.target.value as any)}
          >
            {SEVERITY_OPTIONS.map(s => (
              <option key={s} value={s}>{s || '— all —'}</option>
            ))}
          </select>
        </div>
        <div className="form-group" style={{ marginBottom: 0, minWidth: 200 }}>
          <label htmlFor="as-type">Signal type (substring)</label>
          <input
            id="as-type"
            type="text"
            placeholder="e.g. giftVelocity"
            value={signalType}
            onChange={(e) => setSignalType(e.target.value)}
          />
        </div>
        <button
          className="btn btn-ghost btn-sm"
          onClick={load}
          disabled={loading}
          title="Refresh"
          style={{ alignSelf: 'flex-end' }}
        >
          {loading ? <span className="spinner" /> : '↻'} Refresh
        </button>
        <span
          style={{
            marginLeft: 'auto',
            alignSelf: 'flex-end',
            fontSize: 12,
            color: 'var(--text-muted)',
          }}
        >
          {loading ? 'Loading…' : `${signals.length} signals`}
        </span>
      </div>

      {endpointMissing && (
        <div className="stub-banner">
          Endpoint <code>GET /admin/abuse-signals</code> is not available on this
          server build. Backend reference: Sprint 36 D3 (admin-routes.mjs).
        </div>
      )}

      {error && <div className="error-banner">{error}</div>}

      {!loading && !endpointMissing && !error && signals.length === 0 && (
        <div className="empty-state">
          <div style={{ fontSize: 32, marginBottom: 12 }}>OK</div>
          <div
            style={{
              fontSize: 16,
              fontWeight: 600,
              color: 'var(--text-secondary)',
              marginBottom: 6,
            }}
          >
            No abuse signals match the current filters
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            {status === 'open'
              ? 'Anti-cheat detectors have not flagged anything for review.'
              : 'Adjust filters or switch status to see other signals.'}
          </div>
        </div>
      )}

      {signals.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Severity</th>
                <th>Type</th>
                <th>Gang</th>
                <th>Detected</th>
                <th>Disposition</th>
                <th>Reviewer</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {signals.map(s => {
                const isOpen = expanded.has(s.signal_id);
                const hasDetails = s.details && Object.keys(s.details).length > 0;
                return (
                  <>
                    <tr key={s.signal_id}>
                      <td>{severityBadge(s.severity)}</td>
                      <td>
                        <span className="text-mono" style={{ fontSize: 12 }}>
                          {s.signal_type}
                        </span>
                      </td>
                      <td>
                        <span className="text-mono" style={{ fontSize: 11 }}>
                          {s.gang_id.slice(0, 12)}{s.gang_id.length > 12 ? '…' : ''}
                        </span>
                      </td>
                      <td className="text-muted" style={{ whiteSpace: 'nowrap' }}>
                        <span title={s.detected_at}>{timeAgo(s.detected_at)}</span>
                      </td>
                      <td>{dispositionBadge(s.disposition)}</td>
                      <td className="text-mono text-muted" style={{ fontSize: 11 }}>
                        {s.reviewer_account_id
                          ? `${s.reviewer_account_id.slice(0, 10)}…`
                          : '—'}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {hasDetails && (
                            <button
                              className="btn btn-ghost btn-sm"
                              onClick={() => toggleExpand(s.signal_id)}
                              title="Toggle raw detection details"
                            >
                              {isOpen ? 'Hide ▲' : 'Details ▼'}
                            </button>
                          )}
                          {!s.disposition && (
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => openReview(s)}
                            >
                              Review
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {isOpen && hasDetails && (
                      <tr key={`${s.signal_id}-expanded`}>
                        <td colSpan={7} style={{ padding: 0 }}>
                          <pre className="raw-payload">
                            {JSON.stringify(
                              {
                                signal_id: s.signal_id,
                                details: s.details,
                                review_notes: s.review_notes ?? null,
                                reviewed_at: s.reviewed_at ?? null,
                              },
                              null,
                              2,
                            )}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Review dialog */}
      <ConfirmDialog
        open={!!reviewTarget}
        title={`Review Abuse Signal`}
        description={
          reviewTarget
            ? `Signal ${reviewTarget.signal_id.slice(0, 12)}… (${reviewTarget.signal_type}, ${reviewTarget.severity}) ` +
              `against gang ${reviewTarget.gang_id.slice(0, 12)}…. ` +
              `Disposition will be recorded immutably; no further review allowed.`
            : undefined
        }
        confirmLabel="Submit Review"
        variant={fieldValues.disposition === 'suspend' ? 'danger' : 'default'}
        loading={reviewLoading}
        fields={[
          {
            key: 'disposition',
            label: 'Disposition',
            type: 'select',
            options: DISPOSITIONS.map(d => ({ value: d, label: d })),
            defaultValue: 'dismiss',
          },
          {
            key: 'notes',
            label: 'Reviewer notes (optional)',
            type: 'textarea',
            placeholder: 'Context, evidence links, follow-up actions...',
          },
        ]}
        fieldValues={fieldValues}
        onFieldChange={(k, v) => setFieldValues(fv => ({ ...fv, [k]: v }))}
        onConfirm={executeReview}
        onCancel={() => setReviewTarget(null)}
      />

      {reviewError && <div className="error-banner mt-8">{reviewError}</div>}
    </div>
  );
}
